import { MdSave } from "react-icons/md";
import { ROUTES } from "../../../routes";
import Utils from "../../../utils/utils";
import DatePicker from "react-datepicker";
import { useState, useContext, useEffect } from "react";
import { HiArrowLeft, HiX } from "react-icons/hi";
import ApiCohort from "../../../modules/api/cohort";
import FGField from "../../../components/dumb/FGField";
import FGInput from "../../../components/dumb/FGInput";
import FGToggle from "../../../components/dumb/FGToggle";
import usePageTitle from "../../../hooks/use-page-title";
import ApiWLFormat from "../../../modules/api/wl-format";
import Notifier from "../../../modules/notifier/notifier";
import DateRange from "../../../components/form/DateRange";
import FGDropdown from "../../../components/dumb/FGDropdown";
import FGTextarea from "../../../components/dumb/FGTextarea";
import Debouncer from "../../../modules/debouncer/debouncer";
import AppUserContext from "../../../context/app-user-context";
import FGDropdownApi from "../../../components/form/FGDropdownApi";
import Multilingual from "../../../modules/multilingual/multilingual";
import { PortalContext, usePortalSettingSet } from "../../../context/portal-context";
import PagePortalNotFound from "../PagePortalNotFound/PagePortalNotFound";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import FooterBoxAction from "../../../components/common/FooterBoxAction";
import { BsBoxArrowInDown } from "react-icons/bs";

export default function PageCohortAdd() {
  const title = "Add New Cohort";
  const navigate = useNavigate();
  usePageTitle(title);
  usePortalSettingSet("label_header", title);
  const { setCustomText } = useContext(PortalContext);
  const location = useLocation();
  const user = useContext(AppUserContext);
  const { programmeId } = useParams();
  const [contentChanged, setContentChanged] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    startDate: null,
    endDate: null,
    firstDate: null,
    weekly: 2,
    description: "",
    singleGroupMode: true,
    pdMode: false,
    wlFormat: null,
    programme: programmeId,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  useEffect(() => {
    setCustomText(title);
  }, []);

  const handleOnChange = (e: any) => {
    setContentChanged(true);
    setFormData((d) => ({
      ...d,
      [e.target.name]: ["singleGroupMode", "pdMode"].includes(e.target.name)
        ? e.target.checked
        : e.target.value,
    }));
  };

  const handleOnChangeFormatOptions = (options: any[]) => {
    if (options.length <= 1) return;
    let defaultOption = options.find((option) => option.label === "Default") ?? options[0];
    setFormData((prev) => ({ ...prev, wlFormat: defaultOption.value }));
  };

  const validateBeforeSubmit = () => {
    const errors = [];
    if (!formData.startDate || !formData.endDate) {
      errors.push("Start/End dates are required");
    } else if (formData.endDate <= formData.startDate) {
      errors.push("End date must be greater than start date");
    }
    if (!formData.firstDate) {
      errors.push("First Submission Date is required");
    } else if (
      formData.startDate &&
      formData.endDate &&
      (formData.firstDate < formData.startDate ||
        formData.firstDate > formData.endDate)
    ) {
      errors.push("First Submission Date must be from start date to end date");
    }
    if (!Number.isInteger(formData.weekly) || !(formData.weekly > 0)) {
      errors.push("Weekly Workplace Learning Report is required");
    }
    if (!formData.wlFormat) {
      errors.push("Please select Workplace Learning Report Format");
    }
    return errors;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = validateBeforeSubmit();
    if (errors.length > 0) {
      Notifier.error(errors[0]);
      // errors.forEach((e) => Notifier.error(e));
      return;
    }
    Debouncer.execute("SUBMIT_DATA", async () => {
      setSending(true);
      const payload = {
        ...formData,
        startDate: formData.startDate.getTime(),
        endDate: formData.endDate.getTime(),
        firstDate: formData.firstDate.getTime(),
      };
      await ApiCohort.create(payload)
        .then(async (res) => {
          setSending(false);
          if (res.result === "OK") {
            Notifier.success(res.message);
            await Utils.sleep(2000);
            navigate(`${ROUTES.PORTAL_COHORTS}/${programmeId}`);
          } else {
            Notifier.error(res.message);
          }
        })
        .catch((error) => {
          setSending(false);
          Notifier.error(error.message);
        });
    });
  };

  if (!user.programmeIds.includes(programmeId)) {
    return <PagePortalNotFound />;
  }
  return (
    <main className="form-generic content-generic">
      {/* <h1 className="fs-title hide-mobile-down">{title}</h1> */}

      {/* <div className="cg__actions hide-mobile-down">
        <Link
          to={`${ROUTES.PORTAL_COHORTS}/${programmeId}`}
          className="btn btn-primary"
          state={location.state}
          onClick={(e) => {
            if (
              contentChanged &&
              !window.confirm(Multilingual.get("CONFIRM_ACTION_CANT_BE_UNDONE"))
            ) {
              e.preventDefault();
            }
          }}
        >
          <HiArrowLeft /> Back
        </Link>
      </div> */}

      <form onSubmit={handleSubmit} className="box-default bg-white p-3">
        <FGInput
          name="name"
          id="name"
          type="text"
          label="Name of the Cohort"
          required
          autoFocus
          onChange={handleOnChange}
        />

        <FGTextarea
          name="description"
          id="description"
          label="Description of the Cohort"
          onChange={handleOnChange}
        ></FGTextarea>

        <FGField variant="normal">
          <DateRange
            required
            title="Duration of the IWSP"
            onStartEndDateSelected={(start, end) => {
              setContentChanged(true);
              end.setHours(23, 59, 59, 999);
              setFormData((formData) => ({
                ...formData,
                startDate: start,
                endDate: end,
              }));
            }}
          />
        </FGField>

        <FGDropdownApi
          required
          label="Workplace Learning Report Template"
          name="wlFormat"
          defaultValue={formData.wlFormat}
          api={() => ApiWLFormat.getOptions(programmeId)}
          onChange={handleOnChange}
          onChangeOptions={handleOnChangeFormatOptions}
        />

        <FGField
          required
          name="firstDate"
          label={"End Date (Deadline) to submit the first Workplace Learning Report"}
        >
          <DatePicker
            placeholderText="Select Date"
            dateFormat="MMMM d, yyyy"
            selected={formData.firstDate}
            minDate={formData.startDate}
            maxDate={formData.endDate}
            onChange={(firstDate) => {
              firstDate.setHours(23, 59, 59, 999);
              setFormData((formData) => ({
                ...formData,
                firstDate: firstDate,
              }));
            }}
          />
        </FGField>

        <FGDropdown
          required
          name="weekly"
          label={
            "Students are required to submit a Workplace Learning Report every:"
          }
          defaultValue={`${formData.weekly}`}
          options={[
            {
              label: "1 week",
              value: "1",
            },
            {
              label: "2 weeks",
              value: "2",
            },
            {
              label: "3 weeks",
              value: "3",
            },
            {
              label: "4 weeks",
              value: "4",
            },
          ]}
          onChange={(e: any) =>
            setFormData((formData) => ({
              ...formData,
              weekly: parseInt(e.target.value),
            }))
          }
        />

        {/* We no longer support Group Mode: IS-145 */}
        {false && <FGToggle
            label="Single Group Mode"
            sublabel='When enabled, this cohort will have single group named "Default"'
            name="singleGroupMode"
            onChange={handleOnChange}
            checked={formData.singleGroupMode}
        ></FGToggle>}

        <FGToggle
          label="Professional Development Report using the GROW Model"
          sublabel="Enable the above setting if the Cohort of this Programme requires the Professional Development feature of GROW-Pro for Students and Supervisors."
          name="pdMode"
          onChange={handleOnChange}
          checked={formData.pdMode}
        ></FGToggle>

        <div>
          <button type="submit" className="btn btn-secondary" disabled={sending}>
            <BsBoxArrowInDown className="icon-pos-upper" /> Save
          </button>{" "}
          <Link
            to={`${ROUTES.PORTAL_COHORTS}/${programmeId}`}
            className="btn btn-primary"
            state={location.state}
            onClick={(e) => {
              if (
                contentChanged &&
                !window.confirm(
                  Multilingual.get("CONFIRM_ACTION_CANT_BE_UNDONE")
                )
              ) {
                e.preventDefault();
              }
            }}
          >
            <HiX /> Cancel
          </Link>
        </div>
      </form>
      <FooterBoxAction backUrl={`${ROUTES.PORTAL_COHORTS}/${programmeId}`}
        backUrlState={location.state}
        onBackUrlClick={(e) => {
          if (
            contentChanged &&
              !window.confirm(
                Multilingual.get("CONFIRM_ACTION_CANT_BE_UNDONE")
              )
          ) {
            e.preventDefault();
            }
          }
        }
      />
    </main>
  );
}
